import type {
  KumaHeartbeat,
  KumaHeartbeatResponse,
  KumaMonitor,
  KumaStatusPageResponse,
  OverallSystemStatus,
  ProcessedGroup,
  ProcessedMonitor,
  ServiceStatus,
  StatusPageState,
} from "../types";
import { config } from "../config";
import {
  mockOperationalHeartbeats,
  mockOperationalStatusPage,
} from "./mockData";

/**
 * Determine a monitor's health from its heartbeats and 24h uptime.
 */
export function calculateMonitorStatus(
  heartbeats: KumaHeartbeat[] | undefined,
  uptime24h: number | undefined
): ServiceStatus {
  if (!heartbeats || heartbeats.length === 0) {
    if (uptime24h !== undefined) {
      if (uptime24h >= 0.99) return "operational";
      if (uptime24h >= 0.90) return "degraded";
      return "down";
    }
    return "unknown";
  }

  const latest = heartbeats[heartbeats.length - 1];

  if (latest.status === 0) {
    return "down";
  }
  if (latest.status === 3) {
    return "maintenance";
  }
  if (latest.status === 2) {
    return "pending";
  }

  // If latest is UP (1), check recent failures (e.g. within last 10 heartbeats)
  const recentWindow = heartbeats.slice(-10);
  const failureCount = recentWindow.filter((h) => h.status === 0).length;
  if (failureCount > 0) {
    return "degraded";
  }

  // Also verify 24h uptime if available
  if (uptime24h !== undefined && uptime24h < 0.98) {
    return "degraded";
  }

  return "operational";
}

/**
 * Determine a group's aggregated status based on its child monitors.
 */
export function calculateGroupStatus(monitors: ProcessedMonitor[]): ServiceStatus {
  if (monitors.length === 0) return "unknown";

  const hasDown = monitors.some((m) => m.status === "down");
  if (hasDown) return "down";

  const hasDegraded = monitors.some((m) => m.status === "degraded");
  if (hasDegraded) return "degraded";

  const hasMaintenance = monitors.some((m) => m.status === "maintenance");
  if (hasMaintenance) return "maintenance";

  const allOperational = monitors.every((m) => m.status === "operational");
  if (allOperational) return "operational";

  return "unknown";
}

/**
 * Determine overall system health banner status.
 */
export function calculateOverallStatus(
  groups: ProcessedGroup[],
  hasCriticalIncident: boolean,
  hasWarningIncident: boolean
): OverallSystemStatus {
  // "loading" is reserved for the pre-fetch state; an empty response is not a load in progress.
  if (groups.length === 0) return "unknown";

  if (hasCriticalIncident) return "outage";

  const allMonitors = groups.flatMap((g) => g.monitors);
  const downCount = allMonitors.filter((m) => m.status === "down").length;
  const degradedCount = allMonitors.filter((m) => m.status === "degraded").length;
  const maintenanceCount = allMonitors.filter((m) => m.status === "maintenance").length;

  if (downCount > 0) {
    // If more than 1 monitor is down, consider it a major outage, otherwise degraded
    return downCount > 1 ? "outage" : "degraded";
  }

  if (hasWarningIncident || degradedCount > 0) {
    return "degraded";
  }

  if (maintenanceCount > 0 && maintenanceCount === allMonitors.length) {
    return "maintenance";
  }

  return "operational";
}

/** Cold Cloudflare + Kuma loads can exceed the old 7s abort. */
export const DEFAULT_FETCH_TIMEOUT_MS = 15_000;

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === "AbortError") ||
    (err instanceof Error && err.name === "AbortError")
  );
}

async function fetchJsonOnce<T>(url: string, timeoutMs: number): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new Error(`HTTP error ${res.status}: ${res.statusText}`);
    }
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

async function fetchJsonWithRetry<T>(url: string, timeoutMs: number): Promise<T> {
  try {
    return await fetchJsonOnce<T>(url, timeoutMs);
  } catch (err) {
    if (!isAbortError(err)) throw err;
    return await fetchJsonOnce<T>(url, timeoutMs);
  }
}

/**
 * Fetch raw status page config & group list from Uptime Kuma.
 */
export async function fetchStatusPage(
  slug: string = config.slug,
  baseUrl: string = config.apiBaseUrl,
  timeoutMs = DEFAULT_FETCH_TIMEOUT_MS
): Promise<KumaStatusPageResponse> {
  const url = `${baseUrl}/api/status-page/${encodeURIComponent(slug)}`;
  return fetchJsonWithRetry<KumaStatusPageResponse>(url, timeoutMs);
}

/**
 * Fetch raw heartbeat data from Uptime Kuma.
 */
export async function fetchHeartbeats(
  slug: string = config.slug,
  baseUrl: string = config.apiBaseUrl,
  timeoutMs = DEFAULT_FETCH_TIMEOUT_MS
): Promise<KumaHeartbeatResponse> {
  const url = `${baseUrl}/api/status-page/heartbeat/${encodeURIComponent(slug)}`;
  return fetchJsonWithRetry<KumaHeartbeatResponse>(url, timeoutMs);
}

/**
 * Assemble processed status page state from raw Kuma responses or mock fallbacks.
 */
export function processStatusData(
  statusPageData: KumaStatusPageResponse,
  heartbeatData: KumaHeartbeatResponse,
  groupFilter: string | null = config.groupFilter,
  isMock = false
): StatusPageState {
  let groupsToProcess = statusPageData.publicGroupList;

  // Filter groups if a group filter is active and present in the data
  if (groupFilter && groupFilter.trim() !== "") {
    const filtered = groupsToProcess.filter(
      (g) => g.name.toLowerCase() === groupFilter.toLowerCase()
    );
    if (filtered.length > 0) {
      groupsToProcess = filtered;
    }
  }

  const processedGroups: ProcessedGroup[] = groupsToProcess.map((group) => {
    const monitors: ProcessedMonitor[] = group.monitorList.map((m: KumaMonitor) => {
      const heartbeats = heartbeatData.heartbeatList[String(m.id)] || [];
      const rawUptime = heartbeatData.uptimeList[`${m.id}_24`];
      const uptimePercent =
        rawUptime !== undefined
          ? Math.round(rawUptime * 10000) / 100
          : heartbeats.length > 0
          ? Math.round(
              (heartbeats.filter((h) => h.status === 1).length / heartbeats.length) * 10000
            ) / 100
          : 100;

      const latest = heartbeats[heartbeats.length - 1];
      const currentPingMs =
        latest && typeof latest.ping === "number" ? Math.round(latest.ping * 100) / 100 : null;
      const lastCheckTime = latest ? latest.time : null;
      const status = calculateMonitorStatus(heartbeats, rawUptime);

      return {
        id: m.id,
        name: m.name,
        groupName: group.name,
        type: m.type,
        status,
        uptimePercent,
        currentPingMs,
        heartbeats,
        lastCheckTime,
      };
    });

    return {
      id: group.id,
      name: group.name,
      status: calculateGroupStatus(monitors),
      monitors,
    };
  });

  const hasCriticalIncident =
    statusPageData.incident !== null && statusPageData.incident.style === "danger";
  const hasWarningIncident =
    statusPageData.incident !== null && statusPageData.incident.style === "warning";

  const overallStatus = calculateOverallStatus(
    processedGroups,
    hasCriticalIncident,
    hasWarningIncident
  );

  const allMonitors = processedGroups.flatMap((g) => g.monitors);
  const overallUptimePercent =
    allMonitors.length > 0
      ? Math.round(
          (allMonitors.reduce((acc, m) => acc + m.uptimePercent, 0) / allMonitors.length) * 100
        ) / 100
      : 100;

  return {
    overallStatus,
    overallUptimePercent,
    groups: processedGroups,
    incident: statusPageData.incident,
    maintenanceList: statusPageData.maintenanceList || [],
    lastUpdated: new Date(),
    isMock,
    isLoading: false,
    error: null,
  };
}

/**
 * Load status page state: live API when online, mock fixtures ONLY when explicitly forced/configured.
 */
export async function loadStatusPageState(options?: {
  forceMock?: boolean;
}): Promise<StatusPageState> {
  const useMock = options?.forceMock ?? config.isMockDefault;

  if (useMock) {
    return processStatusData(
      mockOperationalStatusPage,
      mockOperationalHeartbeats,
      null, // Show all groups in mock
      true
    );
  }

  const [statusData, heartbeatData] = await Promise.all([
    fetchStatusPage(),
    fetchHeartbeats(),
  ]);
  const state = processStatusData(statusData, heartbeatData, config.groupFilter, false);
  // An empty group list (e.g. Kuma mid-restart) is a failed refresh, not an empty system:
  // throwing keeps the last good data on screen instead of wiping every service card.
  if (state.groups.length === 0) {
    throw new Error("a monitoring kiszolgáló üres szolgáltatáslistát adott vissza");
  }
  return state;
}
